import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/race-tracker/components/ui/dialog';
import { Input } from '@/race-tracker/components/ui/input';
import { Label } from '@/race-tracker/components/ui/label';
import { Button } from '@/race-tracker/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/race-tracker/components/ui/select';
import { KNOWN_REP_FIRMS } from '@/big-political-list/lib/stationUtils';

const EMPTY_STATION = {
  state: '', market: '', station: '', affiliate: '', rep_firm: '', rep: '',
  rep_phone: '', rep_email: '', ownership: '',
  cd_contact_name: '', cd_contact_title: '', cd_contact_phone: '', cd_contact_email: '',
  station_address: '', station_city: '', station_state: '', station_zip: '',
};

export default function StationFormModal({ open, onClose, station, onSave, saving }) {
  const [form, setForm] = useState(EMPTY_STATION);
  const [useCustomFirm, setUseCustomFirm] = useState(false);

  useEffect(() => {
    if (station) {
      setForm({ ...EMPTY_STATION, ...station });
      setUseCustomFirm(!KNOWN_REP_FIRMS.includes(station.rep_firm) && !!station.rep_firm);
    } else {
      setForm(EMPTY_STATION);
      setUseCustomFirm(false);
    }
  }, [station, open]);

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  const isEdit = !!station?.id;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold">{isEdit ? 'Edit Rep' : 'Add Rep'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Station Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-medium text-muted-foreground">State *</Label>
              <Input value={form.state} onChange={e => set('state', e.target.value)} required className="mt-1" placeholder="e.g. AL or AL/GA" />
            </div>
            <div>
              <Label className="text-xs font-medium text-muted-foreground">Market (DMA) *</Label>
              <Input value={form.market} onChange={e => set('market', e.target.value)} required className="mt-1" placeholder="e.g. ATLANTA, GA" />
            </div>
            <div>
              <Label className="text-xs font-medium text-muted-foreground">Station *</Label>
              <Input value={form.station} onChange={e => set('station', e.target.value)} required className="mt-1" placeholder="Call sign" />
            </div>
            <div>
              <Label className="text-xs font-medium text-muted-foreground">Affiliate</Label>
              <Input value={form.affiliate} onChange={e => set('affiliate', e.target.value)} className="mt-1" placeholder="CBS, FOX, NBC…" />
            </div>
          </div>

          {/* Rep Firm */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-medium text-muted-foreground">Rep Firm</Label>
              {!useCustomFirm ? (
                <div className="mt-1 flex gap-2">
                  <Select value={form.rep_firm} onValueChange={v => set('rep_firm', v)}>
                    <SelectTrigger><SelectValue placeholder="Select firm" /></SelectTrigger>
                    <SelectContent>
                      {KNOWN_REP_FIRMS.map(f => (
                        <SelectItem key={f} value={f}>{f}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button type="button" variant="ghost" size="sm" className="text-xs shrink-0" onClick={() => setUseCustomFirm(true)}>Other</Button>
                </div>
              ) : (
                <div className="mt-1 flex gap-2">
                  <Input value={form.rep_firm} onChange={e => set('rep_firm', e.target.value)} placeholder="Custom firm name" />
                  <Button type="button" variant="ghost" size="sm" className="text-xs shrink-0" onClick={() => setUseCustomFirm(false)}>List</Button>
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs font-medium text-muted-foreground">Rep</Label>
              <Input value={form.rep} onChange={e => set('rep', e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs font-medium text-muted-foreground">Rep Phone</Label>
              <Input value={form.rep_phone} onChange={e => set('rep_phone', e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs font-medium text-muted-foreground">Rep Email</Label>
              <Input value={form.rep_email} onChange={e => set('rep_email', e.target.value)} className="mt-1" />
            </div>
          </div>

          {/* Ownership */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-medium text-muted-foreground">Ownership</Label>
              <Input value={form.ownership} onChange={e => set('ownership', e.target.value)} className="mt-1" />
            </div>
          </div>

          {/* C&D Contact */}
          <div>
            <h4 className="text-sm font-semibold text-foreground border-b pb-1 mb-3">C&D Contact</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-muted-foreground">Name</Label>
                <Input value={form.cd_contact_name} onChange={e => set('cd_contact_name', e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs font-medium text-muted-foreground">Title</Label>
                <Input value={form.cd_contact_title} onChange={e => set('cd_contact_title', e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs font-medium text-muted-foreground">Phone</Label>
                <Input value={form.cd_contact_phone} onChange={e => set('cd_contact_phone', e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs font-medium text-muted-foreground">Email</Label>
                <Input value={form.cd_contact_email} onChange={e => set('cd_contact_email', e.target.value)} className="mt-1" />
              </div>
            </div>
          </div>

          {/* Station Address */}
          <div>
            <h4 className="text-sm font-semibold text-foreground border-b pb-1 mb-3">Station Address</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-xs font-medium text-muted-foreground">Address</Label>
                <Input value={form.station_address} onChange={e => set('station_address', e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs font-medium text-muted-foreground">City</Label>
                <Input value={form.station_city} onChange={e => set('station_city', e.target.value)} className="mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs font-medium text-muted-foreground">State</Label>
                  <Input value={form.station_state} onChange={e => set('station_state', e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-medium text-muted-foreground">ZIP</Label>
                  <Input value={form.station_zip} onChange={e => set('station_zip', e.target.value)} className="mt-1" />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onClose(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90">
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Rep'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}